import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Container, Row, Col } from 'react-bootstrap';
import { FaCrown } from 'react-icons/fa';
import api from '../api';
import CryptoJS from 'crypto-js';

// Simple Card component for printing
const PrintableCard = ({ cardData }) => {
  if (!cardData) return null;

  return (
    <Container className="mt-3 p-1 rounded fs-5 bg-teal-700" style={{maxWidth: '400px'}}>
      <div className="text-center text-white mb-2"> Cartella {cardData.paletteNumber}</div>
    </Container>
  );
};

const PrintCardsModal = ({ isOpen, onClose, selectedNumbers }) => {
  const [selectedCardsToPrint, setSelectedCardsToPrint] = useState([]);
  const [cardsData, setCardsData] = useState({});
  const [loading, setLoading] = useState(true);

  const getApiUrl = () => {
    const hashedApiVersion = localStorage.getItem('apiVersion');
    if (hashedApiVersion) {
      const version2Hash = CryptoJS.SHA256('2').toString();
      if (hashedApiVersion === version2Hash) {
        return 'https://phoenix-bingo-2.onrender.com/api';
      }
    }
    return process.env.REACT_APP_API;
  };

  const API = getApiUrl();

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const response = await api.get(`${API}/card/getCards`);
        const cards = response;
        const cardsMap = {};
        cards.forEach(card => {
          cardsMap[card.paletteNumber] = card;
        });
        setCardsData(cardsMap);
      } catch (error) {
        console.error('Error fetching cards:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchCards();
    }
  }, [isOpen, API]);

  const toggleCardSelection = (number) => {
    if (selectedCardsToPrint.includes(number)) {
      setSelectedCardsToPrint(selectedCardsToPrint.filter(n => n !== number));
    } else {
      setSelectedCardsToPrint([...selectedCardsToPrint, number]);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Bingo Cards</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            .print-container {
              padding: 20px;
            }
            .card {
              width: 100%;
              max-width: 800px;
              margin: 0 auto;
              page-break-after: always;
              min-height: 90vh;
              display: flex;
              flex-direction: column;
              justify-content: center;
              padding: 20px 0;
            }
            .card:last-child {
              page-break-after: avoid;
            }
            .card-grid {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              width: 100%;
              max-width: 600px;
              margin: 0 auto;
            }
            .number-cell {
              border: 1px solid black;
              padding: 15px;
              text-align: center;
              font-size: 24px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .header-cell {
              background-color: #0D9488 !important;
              color: white;
              font-weight: bold;
              padding: 10px;
              text-align: center;
              font-size: 28px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .card-header {
              text-align: center;
              margin-bottom: 20px;
            }
            .card-header h3 {
              font-size: 32px;
              margin-bottom: 10px;
            }
            .card-number {
              font-size: 24px;
              margin-bottom: 20px;
            }
            @media print {
              @page {
                size: portrait;
                margin: 0.5in;
              }
              body {
                margin: 0;
                padding: 0;
              }
              .card {
                break-inside: avoid;
                page-break-after: always;
              }
              .card:last-child {
                page-break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${selectedCardsToPrint.map(number => {
              const card = cardsData[number];
              if (!card) return '';
              return `
                <div class="card">
                  <div class="card-header">
                    <h3>PHOENIX BINGO</h3>
                    <div class="card-number">Card #${number}</div>
                  </div>
                  <div class="card-grid">
                    <div class="header-cell">B</div>
                    <div class="header-cell">I</div>
                    <div class="header-cell">N</div>
                    <div class="header-cell">G</div>
                    <div class="header-cell">O</div>
                    ${card.numbers.B.map((num, i) => `
                      <div class="number-cell">${num}</div>
                      <div class="number-cell">${card.numbers.I[i]}</div>
                      <div class="number-cell">${card.numbers.N[i]}</div>
                      <div class="number-cell">${card.numbers.G[i]}</div>
                      <div class="number-cell">${card.numbers.O[i]}</div>
                    `).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[80vw] bg-gray-900 text-white">
          <div className="text-center p-4">Loading cards...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] bg-gray-900 text-white">
        <h2 className="text-xl font-bold mb-1">Select Cards to Print</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1 max-h-[60vh] overflow-y-auto">
          {selectedNumbers.map(number => (
            <div key={number} className="relative">
              <input
                type="checkbox"
                checked={selectedCardsToPrint.includes(number)}
                onChange={() => toggleCardSelection(number)}
                className="absolute top-3 left-2 w-5 h-5 z-10"
              />
              <div data-card-number={number}>
                <PrintableCard cardData={cardsData[number]} />
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            onClick={handlePrint}
            disabled={selectedCardsToPrint.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Print Selected Cards ({selectedCardsToPrint.length})
          </Button>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintCardsModal;